import { MouseEvent, useState } from 'react';

type RiderIds = number[];

interface IUseRidersBulkSelection {
  selectedRiderIds: RiderIds;
  handleCheckboxSelection: (event: MouseEvent, id: number, riderIds: RiderIds) => void;
  handleSelectAllOnPage: (pageRiderIds: RiderIds) => void;
  handleSelectAllFiltered: () => void;
  clearSelection: () => void;
  isAllFilteredSelected: boolean;
  selectedCount: number;
}

export const useRidersBulkSelection = (): IUseRidersBulkSelection => {
  const [selectedRiderIds, setSelectedRiderIds] = useState<RiderIds>([]);
  const [isAllFilteredSelected, setIsAllFilteredSelected] = useState(false);
  const [lastSelectedId, setLastSelectedId] = useState<Nullable<number>>(null);

  const handleCheckboxSelection = (event: MouseEvent, id: number, riderIds: RiderIds) => {
    event.stopPropagation();
    setIsAllFilteredSelected(false);

    let additionalIdsToCheck: RiderIds = [];

    if (event.nativeEvent.shiftKey && lastSelectedId) {
      let shouldAddToSelectedIds = false;

      additionalIdsToCheck = riderIds
        .map((riderId) => {
          if (riderId === id) {
            shouldAddToSelectedIds = false;

            return null;
          }
          if (shouldAddToSelectedIds) {
            return riderId;
          }
          if (riderId === lastSelectedId) {
            shouldAddToSelectedIds = true;

            return riderId;
          }

          return null;
        })
        .filter(Boolean) as RiderIds;
    }

    if (selectedRiderIds.indexOf(id) !== -1) {
      setSelectedRiderIds(selectedRiderIds.filter((_, idx) => idx !== selectedRiderIds.indexOf(id)));
    } else {
      setLastSelectedId(id);
      setSelectedRiderIds(Array.from(new Set([...selectedRiderIds, ...additionalIdsToCheck, id])));
    }
  };

  const handleSelectAllOnPage = (pageRiderIds: RiderIds) => {
    const allSelected = pageRiderIds.every((id) => selectedRiderIds.includes(id));

    if (allSelected) {
      setSelectedRiderIds(selectedRiderIds.filter((id) => !pageRiderIds.includes(id)));
      setIsAllFilteredSelected(false);
    } else {
      setSelectedRiderIds(Array.from(new Set([...selectedRiderIds, ...pageRiderIds])));
    }
    setLastSelectedId(null);
  };

  const handleSelectAllFiltered = () => {
    setIsAllFilteredSelected(true);
  };

  const clearSelection = () => {
    setSelectedRiderIds([]);
    setIsAllFilteredSelected(false);
    setLastSelectedId(null);
  };

  return {
    selectedRiderIds,
    handleCheckboxSelection,
    handleSelectAllOnPage,
    handleSelectAllFiltered,
    clearSelection,
    isAllFilteredSelected,
    selectedCount: isAllFilteredSelected ? Infinity : selectedRiderIds.length,
  };
};
